// Step 2 - the customer's API key (PlusVibe, Instantly, Smartlead, Bison or Warmuptool, per the run).
//
// The key is write-only from here. It goes up once through sqApi.setApiKey, is
// authenticated against the platform before the server stores it, and never
// comes back on any response. This component holds it in state only while the
// input is on screen and clears that state the instant the server confirms, so a
// verified customer credential is not sitting in a React tree for the rest of
// the session.
//
// Platform differences worth knowing: a PlusVibe key can see several workspaces;
// an Instantly key IS one workspace, so its "list" has exactly one entry and
// step 4 becomes the confirmation it is the right one; a Smartlead key has no
// workspace list at all - validating it captures the workspace user id instead.
//
// Bison adds a second field. That platform is white-labeled onto the customer's
// own instance, so there is no central API host to send the key to: the
// instance URL and the key are only meaningful as a pair and are verified
// together, which is why KEY_INVALID there can mean either half is wrong. The
// URL is NOT a secret - it comes back on every read, seeds this input from the
// run and stays visible, unlike the key beside it. Warmuptool is self-hosted, so
// it takes the same pair of fields, and has no workspace list to fetch.

import { useState } from 'react';

import { sqApi, sqErrorCode, sqErrorMessage } from '../sequencerApi';
import type { SqStepProps } from './shared';

const KEY_INPUT_ID = 'sq-api-key';
const KEY_HINT_ID = 'sq-api-key-hint';
const URL_INPUT_ID = 'sq-bison-url';
const URL_HINT_ID = 'sq-bison-url-hint';

function keyInvalidMessage(seqName: string): string {
  return `${seqName} denied this API key. Get the actual API key from the customer and try again.`;
}

export function StepApiKey({ runId, detail, reload, goToStep, toast }: SqStepProps): JSX.Element {
  const { run } = detail;
  const isBison = run.sequencer === 'bison';
  const isWarmuptool = run.sequencer === 'warmuptool';
  const needsUrl = isBison || isWarmuptool;
  const seqName =
    run.sequencer === 'instantly'
      ? 'Instantly'
      : run.sequencer === 'smartlead'
        ? 'Smartlead'
        : isBison
          ? 'Bison'
          : isWarmuptool
            ? 'Warmuptool'
            : 'PlusVibe';

  const [apiKey, setApiKey] = useState('');
  // Seeded from the run and kept on screen: the instance origin is stored in
  // the clear, so re-validating a key does not mean retyping the URL.
  const [baseUrl, setBaseUrl] = useState(run.base_url ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function validate(): Promise<void> {
    const value = apiKey.trim();
    if (value === '') return;
    if (needsUrl && baseUrl.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      const result = await sqApi.setApiKey(runId, value, needsUrl ? baseUrl.trim() : undefined,
        { resolveSmartleadUserId: run.sequencer === 'smartlead' });
      // Cleared before anything else: from this point the browser holds no copy.
      setApiKey('');
      // Raised as a toast rather than an inline notice because this step is
      // about to be replaced by step 3 - an inline success message would unmount
      // before it could be read.
      toast(
        'success',
        run.sequencer === 'instantly'
          ? `API key valid - workspace "${result.workspaces[0]?.name ?? 'unknown'}".`
          : run.sequencer === 'smartlead'
            ? `API key valid - user ID ${result.sl_user_id ?? 'unavailable'}.`
            : isWarmuptool
              ? 'API URL and key valid.'
              : `API key valid - ${result.workspaces.length} workspace(s) visible.`,
      );
      await reload();
      await goToStep(3);
    } catch (err) {
      // KEY_INVALID is the operator's mistake and has a fix; every other failure
      // (the sequencer down, run closed) is the server's own words, already
      // scrubbed of the key before it left the router.
      setError(
        sqErrorCode(err) !== 'KEY_INVALID'
          ? sqErrorMessage(err)
          : isBison
            ? 'Bison denied this URL + key pair. Check the instance URL and get the actual API key from the customer.'
            : isWarmuptool
              ? 'Warmuptool denied this URL + key pair. Check it is the API origin, not the dashboard, and that the key has READ_EMAILS and WRITE_EMAILS.'
              : keyInvalidMessage(seqName),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 2 - {needsUrl ? `${seqName} instance and API key` : `${seqName} API key`}</h2>
        <p>
          {needsUrl
            ? `Paste the ${isBison ? "customer's Bison instance URL and their" : 'Warmuptool API URL and its'} API key. They are checked together against that instance, and a pair that fails is not stored at all.`
            : `Paste the customer's ${seqName} API key. It is authenticated against ${seqName} before it is stored, and a key that fails is not stored at all.`}
        </p>
      </div>
      <div className="sq-step-body">
        {run.has_api_key && (
          <div className="sq-notice sq-notice--ok">
            A validated key is already held for this run. You only need to enter one again if it was
            purged, or if the customer issued a new key.
          </div>
        )}

        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        {needsUrl && (
          <div className="sq-field">
            <label className="sq-field-label" htmlFor={URL_INPUT_ID}>
              {isBison ? 'Bison instance URL' : 'Warmuptool API URL'}
            </label>
            <input
              id={URL_INPUT_ID}
              aria-describedby={URL_HINT_ID}
              className="sq-input sq-mono"
              type="text"
              value={baseUrl}
              maxLength={300}
              autoComplete="off"
              spellCheck={false}
              placeholder={isBison ? 'https://sequencer.customer.com' : 'https://api.pi-warmup.com'}
              onChange={(e) => setBaseUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !busy) void validate();
              }}
              disabled={busy}
            />
            <span className="sq-hint" id={URL_HINT_ID}>
              {isBison
                ? "The customer's own Bison instance, https only."
                : "The instance's API origin, https only, not the dashboard address."}{' '}
              It is not a secret and stays on the run, but it is validated together with the key as
              a pair - a good key on the wrong instance is refused exactly like a bad one.
            </span>
          </div>
        )}

        <div className="sq-field">
          <label className="sq-field-label" htmlFor={KEY_INPUT_ID}>
            {run.has_api_key ? `Replace the ${seqName} API key` : `${seqName} API key`}
          </label>
          <input
            id={KEY_INPUT_ID}
            aria-describedby={KEY_HINT_ID}
            className="sq-input sq-mono"
            type="password"
            value={apiKey}
            maxLength={500}
            autoComplete="off"
            spellCheck={false}
            placeholder="Paste the key here"
            onChange={(e) => setApiKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) void validate();
            }}
            disabled={busy}
          />
          <span className="sq-hint" id={KEY_HINT_ID}>
            The key is borrowed, not kept. It is held only for the life of this run, is never
            displayed back to anyone, and is destroyed when the run is archived.{' '}
            {run.sequencer === 'instantly'
              ? 'One Instantly key belongs to exactly one workspace - validating it names the workspace step 4 confirms.'
              : run.sequencer === 'smartlead'
                ? 'Validation fills in the user ID for step 4. If no ID is available, it creates a temporary empty draft campaign and removes it after finding the ID.'
                : isWarmuptool
                  ? 'It needs READ_EMAILS and WRITE_EMAILS: validating proves the read half, and Start proves the write half before anything is queued.'
                  : 'Validating it also fetches the list of workspaces it can see, which is what step 4 picks from.'}
          </span>
        </div>

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void validate()}
            disabled={busy || apiKey.trim() === '' || (needsUrl && baseUrl.trim() === '')}
          >
            {busy ? 'Validating...' : 'Validate'}
          </button>
          {run.has_api_key && (
            <button
              type="button"
              className="sq-btn sq-btn--secondary"
              onClick={() => void goToStep(3)}
              disabled={busy}
            >
              Keep this key and continue
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
